import type { ReactNode } from "react";
import { emptyIcon } from "@/assets/default-icons";
import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { useI18n } from "@/locales/i18n-provider";
import { isSessionExpiredError } from "@/request/request-client/session-expired";
import { resolveRequestErrorDisplay } from "./request-error-display";

export { resolveRequestErrorDisplay } from "./request-error-display";

/** RequestBoundary 需要的 react-query 结果子集（避免强绑定具体泛型形态）。 */
export type QueryLike<TData> = {
  isPending: boolean;
  isError: boolean;
  isFetching?: boolean;
  error: unknown;
  data: TData | undefined;
  refetch: () => unknown;
};

export type EmptyConfig = {
  /** 缺省插画 src；缺省用 emptyIcon。 */
  illustration?: string;
  /** 自定义图标节点（如 lucide 图标）；提供时覆盖 illustration。 */
  icon?: ReactNode;
  title: string;
  description?: string;
  actions?: ReactNode;
};

type RequestBoundaryProps<TData> = {
  query: QueryLike<TData>;
  /** 加载时的文案。 */
  loadingLabel?: string;
  /** 页面需要保持布局时，可覆盖默认的居中加载态。 */
  loadingFallback?: ReactNode;
  /** 判断数据是否为空（如列表长度为 0）；配合 `empty` 展示缺省态。 */
  isEmpty?: (data: TData) => boolean;
  empty?: EmptyConfig;
  children: ReactNode | ((data: TData) => ReactNode);
};

/** 居中占满内容区，承载 加载 / 错误 / 空 三种缺省态。 */
function Centered({ children }: { children: ReactNode }) {
  return <div className="grid h-full place-items-center p-6">{children}</div>;
}

/**
 * 统一处理一次数据请求的加载 / 错误（可重试与否）/ 空态，成功时渲染 children。
 * 错误缺省图按错误类型自动选择（403 无权限、404 不存在、其余可重试的加载失败）。
 */
export function RequestBoundary<TData>({ query, loadingLabel, loadingFallback, isEmpty, empty, children }: RequestBoundaryProps<TData>) {
  const { t } = useI18n();
  const resolvedLoadingLabel = loadingLabel ?? t("request.loading");

  if (query.isPending) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }

    return (
      <Centered>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader className="text-current" label={resolvedLoadingLabel} size={16} />
          <span>{resolvedLoadingLabel}</span>
        </div>
      </Centered>
    );
  }

  if (query.isError && !(isSessionExpiredError(query.error) && query.data !== undefined)) {
    const display = resolveRequestErrorDisplay(query.error, t);
    return (
      <Centered>
        <Empty>
          <EmptyHeader>
            <EmptyMedia>
              <img alt="" className="h-32 w-auto" src={display.illustration} />
            </EmptyMedia>
            <EmptyTitle>{display.title}</EmptyTitle>
            <EmptyDescription>{display.description}</EmptyDescription>
          </EmptyHeader>
          {display.retryable ? (
            <EmptyContent>
              <Button onClick={() => query.refetch()}>{t("request.retry")}</Button>
            </EmptyContent>
          ) : null}
        </Empty>
      </Centered>
    );
  }

  const data = query.data as TData;

  if (isEmpty?.(data)) {
    return (
      <Centered>
        <Empty>
          <EmptyHeader>
            {/* 传图标时用带底色的方框（EmptyMedia 的 icon 变体），传插画时保持原尺寸。 */}
            {empty?.icon ? (
              <EmptyMedia variant="icon">{empty.icon}</EmptyMedia>
            ) : (
              <EmptyMedia>
                <img alt="" className="h-32 w-auto" src={empty?.illustration ?? emptyIcon} />
              </EmptyMedia>
            )}
            <EmptyTitle>{empty?.title ?? t("request.empty")}</EmptyTitle>
            {empty?.description ? <EmptyDescription>{empty.description}</EmptyDescription> : null}
          </EmptyHeader>
          {empty?.actions ? <EmptyContent>{empty.actions}</EmptyContent> : null}
        </Empty>
      </Centered>
    );
  }

  return <>{typeof children === "function" ? children(data) : children}</>;
}
